/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-10-20 17:18:06
 */
import React, { useState, useRef } from 'react'
import { Checkbox, Empty, Button, message } from 'antd'
import { AutoSizer, Grid } from 'react-virtualized'
import { setLoading } from '../../../actions/main'
import { setPhotos, deletePhoto, fetchMorePhotos } from '../../../actions/photo'
import actionTypes from '../../../constants/actionTypes'
import CONFIG from '../../../constants/config'
import Viewer from '../ViewerV2'
import Photo from '../Photo'
import backTop from '../../../images/back_top.png'
import { capturePromiseException } from '../../../utils/sentryUtils'
import { formatNum } from '../../../utils/utils'
import { buildFetchPhotosParams } from '@/pages/components/photoLib/photo'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'

function PhotosList(props) {
  const {
    photoListRef,
    multipleSelect = true,
    handleOnePickSubmit,
    from,
    isFromAiSecurity,
    aiSecurityMultiSelect,
    setAiSecurityMultiSelect,
    maxSelectNum = 0,
    canFavorite,
    handleSensorOfClickEvent
  } = props

  const dispatch = useAppDispatch()
  const photos = useAppSelector((state) => state.photo.photos)
  const photosNum = useAppSelector((state) => state.photo.photosNum)
  const checkedPhotos = useAppSelector((state) => state.photo.checkedPhotos)
  const unCheckedPhotos = useAppSelector((state) => state.photo.unCheckedPhotos)
  const checkAll = useAppSelector((state) => state.photo.checkAll)
  const lastPageCond = useAppSelector((state) => state.photo.lastPageCond)
  const queryFilter = useAppSelector((state) => state.photo.queryFilter)
  const groupId = useAppSelector((state) => state.group.groupId)

  const [autoSizerWidth, setAutoSizerWidth] = useState(0)
  const [visibleBackTop, setVisibleBackTop] = useState(false)

  const scrollFetchingRef = useRef(false)
  const viewerRef = useRef<{ view: (index?: number) => void } | null>(null)

  const handlePushCheckedPhoto = (photo) => {
    // AI 安全页面的特殊逻辑：只有开启多选时才能进行多选操作
    if (isFromAiSecurity && !aiSecurityMultiSelect) {
      return
    }

    if (!multipleSelect) {
      // 单选模式下不执行勾选操作
      return
    }

    // 检查是否达到最大可选数量限制
    if (maxSelectNum > 0) {
      const currentSelectedCount = Object.keys(checkedPhotos || {}).length
      if (currentSelectedCount >= maxSelectNum) {
        message.error(`最多只能选择${formatNum(maxSelectNum)}张照片`)
        return
      }
    }

    if (checkAll) {
      dispatch({
        type: actionTypes.REMOVE_UNCHECKED_PHOTO,
        data: photo
      })
    } else {
      if (Object.keys(checkedPhotos || {}).length === photosNum - 1) {
        dispatch({
          type: actionTypes.SET_CHECK_ALL,
          data: true
        })
        dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
        dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
      } else {
        dispatch({
          type: actionTypes.PUSH_CHECKED_PHOTO,
          data: photo
        })
      }
    }
  }

  const handleRemoveCheckedPhoto = (photo: any) => {
    if (checkAll) {
      if (Object.keys(unCheckedPhotos || {}).length === photosNum - 1) {
        dispatch({
          type: actionTypes.SET_CHECK_ALL,
          data: false
        })
        dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
        dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
      } else {
        dispatch({
          type: actionTypes.PUSH_UNCHECKED_PHOTO,
          data: photo
        })
      }
    } else {
      dispatch({
        type: actionTypes.REMOVE_CHECKED_PHOTO,
        data: photo
      })
    }
  }

  const handleGridScroll = ({ clientHeight, scrollHeight, scrollTop }) => {
    if (scrollTop > window.screen.height - 210) {
      setVisibleBackTop(true)
    } else {
      setVisibleBackTop(false)
    }

    if (
      !scrollFetchingRef.current &&
      scrollHeight > clientHeight &&
      scrollHeight - clientHeight - scrollTop <= 0
    ) {
      handleFetchMore()
    }
  }

  const handleBackTopClick = () => {
    const dom = document.getElementsByClassName('virtualized-grid')[0]
    dom.scrollTop = 0
  }

  const handleFetchMore = () => {
    if (lastPageCond) {
      if (!scrollFetchingRef.current) {
        scrollFetchingRef.current = true
        dispatch(
          fetchMorePhotos(
            buildFetchPhotosParams(queryFilter as any, groupId, lastPageCond)
          )
        )
          .then((results) => {
            scrollFetchingRef.current = false
          })
          .catch(capturePromiseException)
      }
    }
  }

  const renderCell = ({
    autoSizerWidth,
    columnIndex,
    key,
    rowIndex,
    style
  }: any) => {
    const columnCount = Math.floor(autoSizerWidth / 248)
    const index = columnCount * rowIndex + columnIndex
    const photo = photos[index]
    if (photo) {
      return (
        <div
          style={{ margin: '0 auto', ...style }}
          key={index}
          onClick={(e) => {
            e.stopPropagation()
            if (!multipleSelect && handleOnePickSubmit) {
              handleOnePickSubmit(photo)
            }
          }}
        >
          <Photo
            style={{ margin: 0 }}
            photo={photo}
            canCheck={multipleSelect}
            readyToCheck={true}
            onPreview={() => viewerRef.current?.view(index)}
            checked={
              !!checkedPhotos?.[photo.seq] ||
              (checkAll && !unCheckedPhotos?.[photo.seq])
            }
            onCheck={handlePushCheckedPhoto}
            onUnCheck={handleRemoveCheckedPhoto}
          />
        </div>
      )
    } else {
      return null
    }
  }

  const onCheckAll = (e) => {
    handleSensorOfClickEvent('chooseAll')

    dispatch({
      type: actionTypes.SET_CHECK_ALL,
      data: e.target.checked
    })
    dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
    dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
  }

  // AI 安全页面的多选开关处理函数
  const onAiSecurityMultiSelectChange = (checked) => {
    // 切换多选状态
    setAiSecurityMultiSelect(checked)

    // 如果关闭多选，清除所有选择
    if (!checked) {
      dispatch({
        type: actionTypes.SET_CHECK_ALL,
        data: false
      })
      dispatch({
        type: actionTypes.CLEAR_CHECKED_PHOTO
      })
      dispatch({
        type: actionTypes.CLEAR_UNCHECKED_PHOTO
      })
    }
  }

  let checkedCount = Object.keys(checkedPhotos || {}).length

  if (checkAll) {
    checkedCount = photos.length - Object.keys(unCheckedPhotos || {}).length
  }

  return (
    <>
      <div
        className="photoList"
        style={{ paddingTop: multipleSelect ? 0 : 24 }}
      >
        {from && from.includes('ai-security') && (
          <div
            style={{
              marginTop: multipleSelect ? 24 : 0,
              marginBottom: 24,
              flexDirection: 'row',
              width: '100%'
            }}
          >
            <Button
              type={aiSecurityMultiSelect ? 'default' : 'primary'}
              size="large"
              style={{
                marginRight: 20,
                borderRadius: 4
              }}
              onClick={() =>
                onAiSecurityMultiSelectChange(!aiSecurityMultiSelect)
              }
            >
              {aiSecurityMultiSelect ? '取消批量选择' : '批量选择'}
            </Button>

            {!aiSecurityMultiSelect && (
              <span style={{ color: '#999' }}>
                最多支持{formatNum(maxSelectNum ?? 0)}张
              </span>
            )}

            {aiSecurityMultiSelect && (
              <span>
                已选择 <strong>{formatNum(checkedCount)}</strong>{' '}
                张照片，最多支持{formatNum(maxSelectNum ?? 0)}张
                {checkedCount > 0 && (
                  <span
                    style={{
                      color: '#0093FF',
                      cursor: 'pointer',
                      marginLeft: 8
                    }}
                    onClick={() => {
                      dispatch({
                        type: actionTypes.CLEAR_CHECKED_PHOTO
                      })
                      dispatch({
                        type: actionTypes.SET_CHECK_ALL,
                        data: false
                      })
                    }}
                  >
                    取消选择
                  </span>
                )}
              </span>
            )}
          </div>
        )}

        {(!from || !from.includes('ai-security')) &&
          photos.length > 0 &&
          multipleSelect && (
            <div
              style={{
                marginTop: 24,
                marginBottom: 12
              }}
            >
              <Checkbox
                style={{ marginRight: 10 }}
                disabled={photos.length === 0}
                indeterminate={
                  !!(checkAll && Object.keys(unCheckedPhotos || {}).length) ||
                  !!(!checkAll && Object.keys(checkedPhotos || {}).length)
                }
                checked={checkAll}
                onChange={onCheckAll}
              >
                全选
              </Checkbox>

              <span>
                已选择 <strong>{formatNum(checkedCount)}</strong> 张照片
                {checkedCount > 0 && (
                  <span
                    style={{
                      color: '#0093FF',
                      cursor: 'pointer',
                      marginLeft: 8
                    }}
                    onClick={() => {
                      dispatch({
                        type: actionTypes.CLEAR_CHECKED_PHOTO
                      })
                      dispatch({
                        type: actionTypes.SET_CHECK_ALL,
                        data: false
                      })
                    }}
                  >
                    取消选择
                  </span>
                )}
              </span>
            </div>
          )}

        <AutoSizer onResize={({ width }) => setAutoSizerWidth(width)}>
          {({ width, height }) => {
            const columnCount = Math.floor(width / 248)
            const rowCount = Math.ceil(photos.length / columnCount)
            return (
              <Grid
                className="virtualized-grid"
                ref={photoListRef}
                style={{ outline: 'none', scrollBehavior: 'smooth' }}
                width={width}
                height={height}
                columnWidth={248}
                rowHeight={310}
                columnCount={columnCount}
                rowCount={rowCount}
                cellRenderer={({ columnIndex, key, rowIndex, style }) =>
                  renderCell({
                    autoSizerWidth: width,
                    columnIndex,
                    key,
                    rowIndex,
                    style
                  })
                }
                onScroll={handleGridScroll}
              />
            )
          }}
        </AutoSizer>
        <div
          className="isTop"
          style={{ opacity: visibleBackTop ? '1' : '0', right: '56px' }}
        >
          <img
            style={{ width: '48px' }}
            src={backTop}
            onClick={handleBackTopClick}
            alt=""
          />
        </div>
        {!photos.length && (
          <div className="empty-wrapper">
            <Empty description="无照片" />
          </div>
        )}
      </div>
      <Viewer
        ref={viewerRef}
        canDelete={false}
        isHideWatermarkInfo={true}
        photos={photos}
        onFetchMore={handleFetchMore}
        canFavorite={canFavorite}
        afterFavoriteChange={(seq, favorite) => {
          const updatedPhotos = photos.map((photo: any) =>
            photo.seq === seq ? { ...photo, isFavorite: favorite } : photo
          )
          dispatch(setPhotos(updatedPhotos))
        }}
      />
    </>
  )
}

export default PhotosList
